<template>
  <!-- eslint-disable vue/no-v-html -- linkify escapes HTML, so there is no risk of XSS from this content -->
  <span
    v-if="hotlinkUrls"
    :class="[
      'preview-typography',
      preserveWhitespace && 'preview-typography--preserve-whitespace',
    ]"
    :style="style"
    v-html="linkifiedText"
  />
  <!-- eslint-enable -->
  <span
    v-else
    :class="[
      'preview-typography',
      preserveWhitespace && 'preview-typography--preserve-whitespace',
    ]"
    :style="style"
    v-text="text"
  ></span>
</template>

<script lang="ts" setup>
import { getTypographyStyles, TypographyType } from '@owner/theme'
import get from 'lodash/get'
import { computed } from 'vue'

import { linkify } from '@/utils/linkify'

import { useMergedTheme } from './mixins/useMergedThemeComp'

const theme = useMergedTheme()

type Variant =
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'heading4'
  | 'titleLarge'
  | 'titleRegular'
  | 'titleSmall'
  | 'paragraphLarge'
  | 'paragraphRegular'
  | 'paragraphSmall'
  | 'buttonLarge'
  | 'buttonRegular'
  | 'buttonSmall'

const props = withDefaults(
  defineProps<{
    align?: 'left' | 'center' | 'right'
    color?:
      | 'primary'
      | 'secondary'
      | 'placeholder'
      | 'brand'
      | 'success'
      | 'info'
      | 'warning'
      | 'danger'
      | 'white'
      | 'black'
      | 'inherit'
    text: string
    variant: Variant
    hotlinkUrls?: boolean
    preserveWhitespace?: boolean
  }>(),
  {
    align: 'left',
    color: undefined,
    hotlinkUrls: false,
    preserveWhitespace: false,
  }
)

const variantMapping: {
  [v in Variant]: TypographyType
} = {
  buttonLarge: 'buttonLg',
  buttonRegular: 'buttonMd',
  buttonSmall: 'buttonSm',
  heading1: 'title5xl',
  heading2: 'title4xl',
  heading3: 'title3xl',
  heading4: 'title2xl',
  paragraphLarge: 'bodyLg',
  paragraphRegular: 'bodyMd',
  paragraphSmall: 'bodySm',
  titleLarge: 'titleXl',
  titleRegular: 'titleLg',
  titleSmall: 'titleMd',
} as const

const pairing = computed(() => theme.value?.font)

const selectedPairing = computed(() => {
  return props.variant.startsWith('paragraph') ||
    props.variant.startsWith('button')
    ? 'secondary'
    : 'primary'
})

// Serif fonts that should fall back to generic 'serif'
const serifFonts = new Set([
  'Abril Fatface',
  'Cinzel',
  'Lora',
  'Merriweather',
  'Noto Serif Display',
  'Playfair Display',
  'Prata',
  'Solway',
  'Source Serif 4',
])

const fontFamilyName = computed(() => {
  if (selectedPairing.value === 'secondary') {
    return 'Geist'
  }
  return pairing.value?.primary ?? 'Inter'
})

const fontFamilyWithFallback = computed(() => {
  const font = fontFamilyName.value
  const fallback = serifFonts.has(font) ? 'serif' : 'sans-serif'
  // Quote font names that contain spaces
  const quotedFont = font.includes(' ') ? `"${font}"` : font
  return `${quotedFont}, ${fallback}`
})

const typographyStyles = computed(() => {
  return getTypographyStyles('opal', fontFamilyName.value)[
    variantMapping[props.variant]
  ]
})

const typographyColors = {
  brand: 'brand.primary',
  danger: 'danger.400',
  info: 'info.300',
  placeholder: 'text.placeholder',
  primary: 'text.primary',
  secondary: 'text.secondary',
  success: 'success.300',
  warning: 'warning.300',
} as const

const cssColor = computed(() => {
  if (props.color === 'white') {
    return '#ffffff'
  }

  if (props.color === 'black') {
    return '#0D0D0D'
  }

  if (props.color === 'inherit') {
    return undefined
  }

  if (props.color && theme.value)
    return get(theme.value.colors, typographyColors[props.color])

  if (theme.value) {
    return get(theme.value.colors, typographyColors[selectedPairing.value])
  }

  return undefined
})

const style = computed(() => {
  const { fontSize, lineHeight, letterSpacing, weight } = typographyStyles.value
  return {
    color: cssColor.value,
    fontFamily: fontFamilyWithFallback.value,
    fontSize: `${fontSize}rem`,
    fontWeight: weight,
    letterSpacing: `${letterSpacing}rem`,
    lineHeight: `${lineHeight}rem`,
  } as const
})

const linkifiedText = computed(() => {
  if (props.hotlinkUrls) {
    return linkify(props.text, {
      className: 'preview-typography__link',
    })
  }

  return props.text
})
</script>

<style lang="scss">
/* Unscoped because the <a> tags generated by linkify don't have the scoped styles attribute */
.preview-typography .preview-typography__link {
  color: var(--misc-brand);
}

/* Load Geist font (local) */
@font-face {
  font-family: 'Geist';
  src: url('/fonts/Geist-Variable.ttf') format('truetype');
  font-weight: 100 900;
}

/* Load all fonts for preview - using variable/multiple weights to support font-weight: 500 */
/* NOTE: Keep in sync with StandaloneHTMLPreview.vue and main.scss - updated by scripts/add-google-font.ts */
@import url('https://fonts.googleapis.com/css2?family=Abril+Fatface&family=Archivo+Narrow:wght@400;500;600;700&family=Cabin:wght@400;500;600;700&family=Cardo:wght@400;700&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=IBM+Plex+Sans:wght@400;500;600;700&family=Inter:wght@400;500;600;700&family=Lora:wght@400;500;600;700&family=Manrope:wght@400;500;600;700&family=Merriweather:wght@400;700&family=Montserrat:wght@400;500;600;700&family=Noto+Serif+Display:wght@400;500;600;700&family=Nunito:wght@400;500;600;700&family=Oswald:wght@400;500;600;700&family=Playfair+Display:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&family=Raleway:wght@400;500;600;700&family=Roboto:wght@400;500;700&family=Source+Serif+4:opsz,wght@8..60,400;8..60,500;8..60,600;8..60,700&family=Syne:wght@400;500;600;700&family=Bebas+Neue:wght@400;500;600;700&family=Unbounded:wght@400;500;600;700&family=Teko:wght@400;500;600;700&family=BBH+Hegarty:wght@400;500;600;700&family=Michroma:wght@400;500;600;700&family=Pacifico:wght@400;500;600;700&family=Lobster:wght@400;500;600;700&family=Chicle:wght@400;500;600;700&family=Anton:wght@400;500;600;700&family=GFS+Neohellenic:wght@400;500;600;700&family=Cinzel:wght@400;500;600;700&family=Lobster+Two:wght@400;500;600;700&family=Permanent+Marker:wght@400;500;600;700&family=Prata:wght@400;500;600;700&family=Viga:wght@400;500;600;700&family=Chango:wght@400;500;600;700&family=Macondo:wght@400;500;600;700&family=Homemade+Apple:wght@400;500;600;700&family=Lemonada:wght@400;500;600;700&family=Shantell+Sans:wght@400;500;600;700&family=Crushed:wght@400;500;600;700&family=Boogaloo:wght@400;500;600;700&family=Coiny:wght@400;500;600;700&family=Concert+One:wght@400;500;600;700&family=Rajdhani:wght@400;500;600;700&family=Solway:wght@400;500;600;700&family=Graduate:wght@400;500;600;700&family=Alfa+Slab+One:wght@400;500;600;700&family=Jura:wght@400;500;600;700&family=UnifrakturMaguntia&display=swap');
</style>
<style lang="scss" scoped>
.preview-typography {
  cursor: default;

  &--preserve-whitespace {
    white-space: pre-line;
  }
}
</style>
